import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import "./CustomSlider.scss";
import { mergeClassNames } from "../../../utils/commonFunctions";
import CustomToolTip from "../CustomToolTip";

interface SliderProps {
    min?: number;
    max?: number;
    value?: number;
    step?: number;
    disabled?: boolean;
    width?: number; // pixel width of slider
    trackHeight?: number;
    thumbSize?: number;
    onChange?: (value: number) => void;
    className?: string
    trackClassName?: string
    thumbClassName?: string
    thumbRef?: React.RefObject<HTMLDivElement>
    showTooltip?: boolean
    content?: string
    tooltipPlacement?: 'bottom' | 'top'
}

const CustomSlider: React.FC<SliderProps> = ({
    min = 0,
    max = 100,
    value = 0,
    step = 1,
    disabled = false,
    width = 300,
    trackHeight = 8,
    thumbSize = 16,
    onChange = () => { },
    className = '',
    thumbClassName = '',
    trackClassName = '',
    thumbRef,
    showTooltip = true,
    content = '',
    tooltipPlacement = 'top'
}) => {
    const sliderRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const percent = useMemo(() => ((value - min) / (max - min)) * 100, [value, min, max]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !sliderRef.current) return;
        const rect = sliderRef.current.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const clamped = Math.max(0, Math.min(offsetX, rect.width));
        const newValue = min + (clamped / rect.width) * (max - min);
        const rounded = Math.round(newValue / step) * step;
        onChange(rounded);
    }, [isDragging, min, max, step, onChange]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (disabled) return;
        setIsDragging(true);
    }, [disabled]);

    // Event listeners with proper cleanup
    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove, { passive: true });
            window.addEventListener("mouseup", handleMouseUp, { passive: true });

            return () => {
                window.removeEventListener("mousemove", handleMouseMove);
                window.removeEventListener("mouseup", handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
        <div
            className={mergeClassNames(`custom__slider__container ${disabled && "disabled"}`, className)}
            style={{ width: `${width}px`, height: `${trackHeight}px` }}
            ref={sliderRef}
            onMouseDown={(e) => {
                if (disabled) return;
                handleMouseMove(e.nativeEvent);
                handleMouseDown(e);
            }}
        >
            <div className={mergeClassNames(`custom__slider__track`, trackClassName)} />
            <div
                ref={thumbRef}
                className={mergeClassNames(`custom__slider__thumb`, thumbClassName)}
                style={{ left: `${percent}%`, width: `${thumbSize}px`, height: `${thumbSize}px` }}
                onMouseDown={handleMouseDown}
            >
                {showTooltip && (
                    <CustomToolTip title={content} placement={tooltipPlacement}>
                        <div className="custom__slider__thumb__tooltip" style={{ width: `${thumbSize}px`, height: `${thumbSize}px` }} />
                    </CustomToolTip>
                )}
            </div>
        </div>
    );
};

export default CustomSlider